import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import FinanceV2Layout from "./FinanceV2Layout";
import FinanceV2InfoTip from "./FinanceV2InfoTip";
import { NoteState } from "../../context/notes/NoteState";
import FinanceV2Services from "../../UserServices/FinanceV2Services";
import "./FinanceV2.css";

export default function FinanceV2ImportCenter() {
  const { state } = useContext(NoteState);
  const { branch_id, role } = state;
  const [hub, setHub] = useState(null);
  const [health, setHealth] = useState(null);
  const [backfillFrom, setBackfillFrom] = useState("");
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [importRunning, setImportRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSyncLog, setShowSyncLog] = useState(false);
  const [syncLog, setSyncLog] = useState([]);

  const canManage = [1, 3, 5].includes(parseInt(role, 10));
  const salesBf = hub?.sync?.sales_backfill || {};

  const load = async () => {
    setLoading(true);
    try {
      const res = await FinanceV2Services.getDashboard(branch_id);
      setHub(res.data?.data?.hub || res.data?.data);
      const hRes = await FinanceV2Services.getHealthCheck(branch_id);
      setHealth(hRes.data?.data);
    } catch {
      toast.error("Could not load import status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branch_id]);

  useEffect(() => {
    if (showSyncLog) {
      FinanceV2Services.getSyncLog({ per_page: 30, status: "failed" })
        .then((res) => {
          const p = res.data?.data;
          setSyncLog(Array.isArray(p) ? p : p?.data || []);
        })
        .catch(() => setSyncLog([]));
    }
  }, [showSyncLog]);

  const runBackfill = async (processAll = false, retryFailed = false) => {
    setBackfillRunning(true);
    try {
      const res = await FinanceV2Services.backfillSales({
        branch_id,
        from_date: backfillFrom || undefined,
        limit: 1000,
        process_all: processAll,
        retry_failed: retryFailed,
      });
      const d = res.data?.data || {};
      toast.success(`Sales: synced ${d.synced}, failed ${d.failed}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Backfill failed");
    } finally {
      setBackfillRunning(false);
    }
  };

  const runImport = async (fn, label) => {
    setImportRunning(true);
    try {
      const res = await fn({
        branch_id,
        from_date: backfillFrom || undefined,
        process_all: true,
        limit: 1000,
      });
      const d = res.data?.data || {};
      toast.success(`${label}: synced ${d.synced ?? 0}, failed ${d.failed ?? 0}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || `${label} failed`);
    } finally {
      setImportRunning(false);
    }
  };

  const importEverything = async () => {
    if (!window.confirm("Import all modules? This may take several minutes.")) return;
    setImportRunning(true);
    try {
      await FinanceV2Services.importAll({ branch_id, from_date: backfillFrom || undefined });
      toast.success("Import finished");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Import failed");
    } finally {
      setImportRunning(false);
    }
  };

  const retryAllFailed = async () => {
    if (!window.confirm("Retry all failed syncs?")) return;
    setImportRunning(true);
    try {
      await FinanceV2Services.retryAllFailed({
        branch_id,
        from_date: backfillFrom || undefined,
      });
      toast.success("Retry completed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Retry failed");
    } finally {
      setImportRunning(false);
    }
  };

  if (!canManage) {
    return (
      <FinanceV2Layout>
        <p className="text-muted">Import tools are available to finance managers only.</p>
      </FinanceV2Layout>
    );
  }

  return (
    <FinanceV2Layout>
      <div className="fv2-dashboard">
        <header className="fv2-page-header mb-2">
          <div>
            <h1 className="fv2-page-title">Import & sync</h1>
            <p className="fv2-page-sub mb-0">
              Bring historical sales, expenses, payroll, and charity into Finance V2 books.
            </p>
          </div>
          <Link to="/finance-v2/dashboard" className="btn btn-sm btn-outline-secondary">
            Back to overview
          </Link>
        </header>

        {loading ? (
          <p>Loading…</p>
        ) : (
          <>
            {health && (
              <div className={`fv2-card mb-3 ${health.healthy ? "border-success" : "border-danger"}`}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h6 className="mb-0">
                    <i
                      className={`bi me-1 ${health.healthy ? "bi-check-circle text-success" : "bi-exclamation-triangle text-danger"}`}
                    />
                    System check: {health.healthy ? "Ready" : "Needs attention"}
                    <FinanceV2InfoTip
                      title="System check"
                      body="Verifies setup is done, required accounts exist, trial balance balances, and sync errors are cleared."
                      source="Automatic checks on load"
                    />
                  </h6>
                  {!health.healthy && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      disabled={importRunning}
                      onClick={retryAllFailed}
                    >
                      Fix all failed
                    </button>
                  )}
                </div>
                <div className="row g-2 mt-2">
                  {(health.checks || []).map((c) => (
                    <div className="col-md-6" key={c.code}>
                      <div
                        className={`small p-2 rounded ${c.status === "pass" ? "bg-success-subtle" : "bg-danger-subtle"}`}
                      >
                        <strong>{c.label}</strong> — {c.detail}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="fv2-card mb-3">
              <h6 className="fw-bold mb-2">Quick fixes</h6>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={importRunning}
                  onClick={async () => {
                    try {
                      const res = await FinanceV2Services.seedPaymentMethodMappings(branch_id);
                      toast.success(`Payment methods mapped: ${res.data?.seeded ?? 0}`);
                      load();
                    } catch (e) {
                      toast.error(e.response?.data?.message || "Mapping failed");
                    }
                  }}
                >
                  Map payment methods (31 Cash, 33 Card…)
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  disabled={importRunning}
                  onClick={async () => {
                    try {
                      const res = await FinanceV2Services.seedMappingDefaults(branch_id);
                      toast.success(`Mappings seeded: ${res.data?.seeded ?? 0}`);
                      load();
                    } catch (e) {
                      toast.error(e.response?.data?.message || "Mapping failed");
                    }
                  }}
                >
                  Seed all mappings (revenue + expense)
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning"
                  disabled={importRunning}
                  onClick={async () => {
                    try {
                      const res = await FinanceV2Services.repairPayrollSyncLog(branch_id);
                      toast.success(`Payroll sync log repaired: ${res.data?.data?.repaired ?? 0}`);
                      load();
                    } catch (e) {
                      toast.error(e.response?.data?.message || "Repair failed");
                    }
                  }}
                >
                  Repair payroll sync log
                </button>
                <Link to="/finance-v2/mappings" className="btn btn-sm btn-outline-secondary">
                  Mappings & vendors
                </Link>
              </div>
            </div>

            <div className="fv2-card mb-3">
              <label className="form-label small fw-semibold">
                Only import from this date (optional)
                <FinanceV2InfoTip
                  title="From date filter"
                  body="Leave blank to include all eligible records. Set a date to only import transactions on or after that day."
                  source="Your choice"
                />
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ maxWidth: 220 }}
                value={backfillFrom}
                onChange={(e) => setBackfillFrom(e.target.value)}
              />
            </div>

            <div className="fv2-card mb-3">
              <h6 className="fw-bold">
                Sales / orders
                <FinanceV2InfoTip
                  title="Sales import"
                  body="Each order becomes a journal: money to cash/bank, revenue by package type, tax if applicable. New checkouts sync automatically."
                  source="Orders and payments"
                />
              </h6>
              <div className="d-flex flex-wrap gap-3 small mb-3">
                <span>
                  <strong>Eligible:</strong> {salesBf.total_eligible ?? "—"}
                </span>
                <span>
                  <strong>Synced:</strong> {salesBf.synced_success ?? "—"}
                </span>
                <span>
                  <strong>Remaining:</strong> {salesBf.remaining ?? "—"}
                </span>
                <span className="text-danger">
                  <strong>Failed:</strong> {salesBf.sync_failed ?? 0}
                </span>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={backfillRunning || importRunning}
                  onClick={() => runBackfill(false)}
                >
                  Batch (1,000)
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  disabled={backfillRunning || importRunning}
                  onClick={() => runBackfill(true)}
                >
                  Process all remaining
                </button>
                {(salesBf.sync_failed ?? 0) > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-warning"
                    disabled={backfillRunning}
                    onClick={() => runBackfill(false, true)}
                  >
                    Retry failed sales
                  </button>
                )}
              </div>
            </div>

            <div className="row g-3 mb-3">
              {[
                {
                  title: "Legacy expenses",
                  tip: "Old expense screen entries posted as expense journals.",
                  fn: () => runImport(FinanceV2Services.importExpenses, "Expenses"),
                },
                {
                  title: "Payroll",
                  tip: "HR salary and advance records from users_finance.",
                  fn: () => runImport(FinanceV2Services.importPayroll, "Payroll"),
                },
                {
                  title: "Charity",
                  tip: "Charity ledger debits and credits.",
                  fn: () => runImport(FinanceV2Services.importCharity, "Charity"),
                },
                {
                  title: "Vostro expenses",
                  tip: "Daily vostro expense sheets.",
                  fn: () => runImport(FinanceV2Services.importVostro, "Vostro"),
                },
                {
                  title: "Bank ledger",
                  tip: "Legacy bank ledger rows → Bank 1010 + Clearing 1090.",
                  fn: () => runImport(FinanceV2Services.importBankLedger, "Bank ledger"),
                },
                {
                  title: "Petty cash",
                  tip: "Petty cash ledger → account 1020 + Clearing.",
                  fn: () => runImport(FinanceV2Services.importPettyCash, "Petty cash"),
                },
                {
                  title: "Liability ledger",
                  tip: "Liability ledger → AP 2000 + Clearing.",
                  fn: () => runImport(FinanceV2Services.importLiabilityLedger, "Liability ledger"),
                },
              ].map((box) => (
                <div className="col-md-6 col-lg-3" key={box.title}>
                  <div className="fv2-import-box h-100">
                    <h6>
                      {box.title}
                      <FinanceV2InfoTip title={box.title} body={box.tip} />
                    </h6>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary w-100"
                      disabled={importRunning || backfillRunning}
                      onClick={box.fn}
                    >
                      Import all
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="fv2-card">
              <button
                type="button"
                className="btn btn-dark me-2"
                disabled={importRunning || backfillRunning}
                onClick={importEverything}
              >
                Import everything
              </button>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => setShowSyncLog(!showSyncLog)}
              >
                {showSyncLog ? "Hide" : "Show"} failed sync log
              </button>
              {showSyncLog && (
                <div className="fv2-table-wrap mt-3">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Module</th>
                        <th>Type</th>
                        <th>ID</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncLog.map((row) => (
                        <tr key={row.id}>
                          <td>{row.source_module}</td>
                          <td>{row.source_type}</td>
                          <td>{row.source_id}</td>
                          <td className="small text-danger">{row.message}</td>
                        </tr>
                      ))}
                      {!syncLog.length && (
                        <tr>
                          <td colSpan={4} className="text-muted">
                            No failed entries
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </FinanceV2Layout>
  );
}
